import * as THREE from 'three';
import {
  Camera,
  Object3D, Scene,
} from 'three';
import {EffectComposer} from "three/examples/jsm/postprocessing/EffectComposer";
import {RenderPass} from "three/examples/jsm/postprocessing/RenderPass";
import {ShaderPass} from "three/examples/jsm/postprocessing/ShaderPass";
import {FXAAShader} from "three/examples/jsm/shaders/FXAAShader";
import {OutlinePass} from "three/examples/jsm/postprocessing/OutlinePass";
import {OutputPass} from "three/examples/jsm/postprocessing/OutputPass";
import {UnrealBloomPass} from "three/examples/jsm/postprocessing/UnrealBloomPass";
import {GammaCorrectionShader} from "three/examples/jsm/shaders/GammaCorrectionShader";

// 着色器配置
export const vertexShader = `
     varying vec2 vUv;
	 void main() {
		vUv = uv;
		gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
	 }
`
// 着色器配置
export const fragmentShader = `
		uniform sampler2D baseTexture;
		uniform sampler2D bloomTexture;
		uniform vec3 glowColor; 

		varying vec2 vUv;

		void main() {
			vec4 baseColor = texture2D(baseTexture, vUv);
			vec4 bloomColor = texture2D(bloomTexture, vUv);

			// 调整辉光颜色
			vec4 glow = vec4(glowColor, 1.0);

			gl_FragColor = baseColor + glow * bloomColor;
		}`

class ViewEffectComposer {
  dom:HTMLElement;
  scene:Scene;
  camera:Camera;
  renderer: THREE.WebGLRenderer
  effectComposer?: EffectComposer
  outlinePass?: OutlinePass
  unrealBloomPass?: UnrealBloomPass
  shaderPass?: ShaderPass
  glowComposer?: EffectComposer
  glowRenderPass?: RenderPass

  constructor( scene:Scene, camera: Camera, domElement:HTMLElement  ) {
    this.scene = scene
    this.camera = camera
    this.dom=domElement;
  }
  setEffectComposer(renderer: THREE.WebGLRenderer){
    this.renderer = renderer
    this.effectComposer = new EffectComposer(renderer,new THREE.WebGLRenderTarget(this.dom.clientWidth, this.dom.clientHeight));
    const renderPass = new RenderPass(this.scene, this.camera);
    this.effectComposer.addPass(renderPass);

    // 添加描边通道
    this.outlinePass = new OutlinePass(new THREE.Vector2(this.dom.clientWidth, this.dom.clientHeight),this.scene, this.camera)
    this.outlinePass.visibleEdgeColor = new THREE.Color('#FF8C00') // 可见边缘的颜色
    this.outlinePass.hiddenEdgeColor = new THREE.Color('#8a90f3') // 不可见边缘的颜色
    this.outlinePass.edgeGlow = 2 // 发光强度
    this.outlinePass.usePatternTexture = false // 是否使用纹理图案
    this.outlinePass.edgeThickness = 1 // 边缘浓度
    this.outlinePass.edgeStrength = 4 // 边缘的强度，值越高边框范围越大
    this.outlinePass.pulsePeriod = 200 // 闪烁频率，值越大频率越低
    this.effectComposer.addPass(this.outlinePass)
    let outputPass = new OutputPass()
    this.effectComposer.addPass(outputPass)
    // 抗锯齿
    let effectFXAA = new ShaderPass(FXAAShader)
    const pixelRatio = this.renderer.getPixelRatio()
    effectFXAA.uniforms.resolution.value.set(1 / (this.dom.clientWidth * pixelRatio), 1 / (this.dom.clientHeight * pixelRatio))
    effectFXAA.renderToScreen = true
    effectFXAA.needsSwap = true
    this.effectComposer.addPass(effectFXAA)

    //创建辉光效果
    this.unrealBloomPass = new UnrealBloomPass(new THREE.Vector2(this.dom.clientWidth, this.dom.clientHeight), 1.5, 0.4, 0.85)
    // 辉光合成器
    const renderTargetParameters = {
      minFilter: THREE.LinearFilter,
      format: THREE.RGBAFormat,
      stencilBuffer: false,
    };
    const glowRender = new THREE.WebGLRenderTarget(this.dom.clientWidth * 2, this.dom.clientHeight * 2, renderTargetParameters)
    this.glowComposer = new EffectComposer(this.renderer, glowRender)
    this.glowComposer.renderToScreen = false
    this.glowRenderPass = new RenderPass(this.scene, this.camera)
    this.glowComposer.addPass(this.glowRenderPass)
    this.glowComposer.addPass(this.unrealBloomPass)
    // 着色器
    this.shaderPass = new ShaderPass(new THREE.ShaderMaterial({
      uniforms: {
        baseTexture: { value: null },
        bloomTexture: { value: this.glowComposer.renderTarget2.texture },
        tDiffuse: { value: null },
        glowColor: { value: null }
      },
      vertexShader,
      fragmentShader,
      defines: {}
    }), 'baseTexture')

    this.shaderPass.material.uniforms.glowColor.value = new THREE.Color();
    this.shaderPass.renderToScreen = true
    this.shaderPass.needsSwap = true


    // //伽马校正通道
    // const gammaPass= new ShaderPass(GammaCorrectionShader);
    // this.effectComposer.addPass(gammaPass);
  }
  // 设置发光模型参数
  setOutlinePass(param: OutlinePassParam){
    if (param.color){
      this.outlinePass.visibleEdgeColor.set(param.color)
    }
    if (param.edgeThickness){
      this.outlinePass.edgeThickness = param.edgeThickness
    }
    if (param.edgeStrength){
      this.outlinePass.edgeStrength = param.edgeStrength
    }
    if (param.pulsePeriod){
      this.outlinePass.edgeStrength = param.pulsePeriod
    }
  }
  // 添加发光模型
  addOutlinePass(object: Object3D){
    if (!this.outlinePass.selectedObjects){
      this.outlinePass.selectedObjects = []
    }
    let sos = this.outlinePass.selectedObjects
    let exit = false
    sos.forEach((so, index) => {
      if (so.uuid === object.uuid) {
        exit = true
      }
    });
    if (!exit){
      sos.push(object)
    }
    this.outlinePass.selectedObjects = sos
  }
  // 删除发光模型
  deleteOutlinePass(object: Object3D){
    let sos2 = this.outlinePass.selectedObjects
    sos2.forEach((so, index) => {
      if (so.uuid === object.uuid) {
        sos2.splice(index, 1); // 使用 splice 方法删除对象
      }
    });
    this.outlinePass.selectedObjects = sos2
  }

  // 设置发光模型参数
  setUnrealBloom(param: UnrealBloomParam){
    if (param.threshold){
      this.unrealBloomPass.threshold = param.threshold
    }
    if (param.strength){
      this.unrealBloomPass.strength = param.strength
    }
    if (param.radius){
      this.outlinePass.edgeStrength = param.radius
    }
  }
}



export interface OutlinePassParam {
  color?: any // 模型描边颜色，默认白色
  edgeThickness?: number // 高亮发光描边厚度
  edgeStrength?: number  // 高亮描边发光强度
  pulsePeriod?: number, // 模型闪烁频率控制，默认0不闪烁
}

export interface UnrealBloomParam {
  threshold?: any // 限制物体开始发光的亮度值
  strength?: number // 发光强度
  radius?: number  // 亮度的发散半径
}

export { ViewEffectComposer };
